<!--
  SAX Import Dialog - Import SAX File

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/SaxImportDialog.vue

  Handles SAX XML file upload and import
  Supports drag-and-drop, progress indication, and error handling
-->

<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card style="min-width: 500px; max-width: 600px;">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <q-icon name="upload_file" class="q-mr-sm" />
          Import SAX File
        </div>
        <q-space />
        <q-btn
          icon="close"
          flat
          round
          dense
          @click="closeDialog"
        />
      </q-card-section>

      <q-card-section>
        <!-- Step 1: File Selection -->
        <div v-if="currentStep === 'select'" class="import-step-select">
          <!-- Drag and Drop Area -->
          <div
            class="drop-zone"
            :class="{ 'drop-zone-active': isDragging }"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
          >
            <q-icon name="cloud_upload" size="4em" color="primary" />
            <div class="text-h6 q-mt-md">Drop SAX file here</div>
            <div class="text-caption text-grey-7 q-mt-sm">or</div>

            <q-btn
              label="Choose File"
              color="primary"
              outline
              class="q-mt-md"
              @click="$refs.fileInput.click()"
            />

            <input
              ref="fileInput"
              type="file"
              accept=".sax,.xml"
              style="display: none"
              @change="handleFileSelect"
            />

            <div class="text-caption text-grey-6 q-mt-md">
              Supported formats: .sax, .xml (max 50MB)
            </div>
          </div>

          <!-- Selected File Info -->
          <div v-if="selectedFile" class="file-info q-mt-md">
            <q-banner class="bg-blue-1" rounded>
              <template #avatar>
                <q-icon name="description" color="primary" />
              </template>
              <div class="text-weight-medium">{{ selectedFile.name }}</div>
              <div class="text-caption">{{ formatFileSize(selectedFile.size) }}</div>
            </q-banner>
          </div>

          <!-- Import Options -->
          <div v-if="selectedFile" class="import-options q-mt-md">
            <q-option-group
              v-model="importMode"
              :options="importModeOptions"
              color="primary"
              class="q-mb-md"
            />

            <q-checkbox
              v-model="validateBeforeImport"
              label="Validate program before importing"
              color="primary"
            />
          </div>
        </div>

        <!-- Step 2: Uploading/Processing -->
        <div v-if="currentStep === 'uploading'" class="import-step-uploading">
          <div class="text-center q-pa-xl">
            <q-spinner-hourglass color="primary" size="4em" />
            <div class="text-h6 q-mt-md">{{ uploadingMessage }}</div>
            <div class="text-caption text-grey-7 q-mt-sm">
              Please wait...
            </div>

            <q-linear-progress
              v-if="uploadProgress > 0"
              :value="uploadProgress / 100"
              color="primary"
              class="q-mt-md"
            />
          </div>
        </div>

        <!-- Step 3: Preview -->
        <div v-if="currentStep === 'preview'" class="import-step-preview">
          <q-banner class="bg-blue-1" rounded>
            <template #avatar>
              <q-icon name="preview" color="primary" />
            </template>
            Import Preview
          </q-banner>

          <div class="preview-stats q-mt-md">
            <q-list bordered separator>
              <q-item>
                <q-item-section avatar>
                  <q-icon name="widgets" color="primary" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>Components</q-item-label>
                  <q-item-label caption>
                    {{ importPreview?.components?.length || 0 }} components will be imported
                  </q-item-label>
                </q-item-section>
              </q-item>

              <q-item>
                <q-item-section avatar>
                  <q-icon name="cable" color="primary" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>Links</q-item-label>
                  <q-item-label caption>
                    {{ importPreview?.links?.length || 0 }} links will be imported
                  </q-item-label>
                </q-item-section>
              </q-item>

              <q-item v-if="importPreview?.kits?.length > 0">
                <q-item-section avatar>
                  <q-icon name="inventory_2" color="primary" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>Kits</q-item-label>
                  <q-item-label caption>
                    {{ importPreview.kits.join(', ') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>

          <!-- Validation Results -->
          <div v-if="validationResults" class="validation-results q-mt-md">
            <q-banner
              :class="validationResults.is_valid ? 'bg-green-1' : 'bg-red-1'"
              rounded
            >
              <template #avatar>
                <q-icon
                  :name="validationResults.is_valid ? 'check_circle' : 'error'"
                  :color="validationResults.is_valid ? 'positive' : 'negative'"
                />
              </template>
              <div class="text-weight-medium">
                {{ validationResults.is_valid ? 'Valid Program' : 'Validation Errors' }}
              </div>
              <div v-if="!validationResults.is_valid" class="q-mt-sm">
                <ul class="q-pl-md">
                  <li
                    v-for="(error, index) in validationResults.errors"
                    :key="index"
                    class="text-caption"
                  >
                    {{ error }}
                  </li>
                </ul>
              </div>
            </q-banner>
          </div>
        </div>

        <!-- Step 4: Success -->
        <div v-if="currentStep === 'success'" class="import-step-success">
          <div class="text-center q-pa-xl">
            <q-icon name="check_circle" size="5em" color="positive" />
            <div class="text-h6 q-mt-md">Import Successful!</div>
            <div class="text-caption text-grey-7 q-mt-sm">
              {{ importResult?.components?.length || 0 }} components and
              {{ importResult?.links?.length || 0 }} links imported
            </div>
          </div>
        </div>

        <!-- Step 5: Error -->
        <div v-if="currentStep === 'error'" class="import-step-error">
          <q-banner class="bg-red-1" rounded>
            <template #avatar>
              <q-icon name="error" color="negative" />
            </template>
            <div class="text-weight-medium">Import Failed</div>
            <div class="text-caption q-mt-sm">
              {{ errorMessage }}
            </div>
          </q-banner>

          <div v-if="errorDetails" class="error-details q-mt-md">
            <q-expansion-item
              label="Error Details"
              icon="info"
              dense
            >
              <q-card>
                <q-card-section class="text-caption">
                  <pre>{{ errorDetails }}</pre>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <!-- Select Step -->
        <template v-if="currentStep === 'select'">
          <q-btn label="Cancel" flat @click="closeDialog" />
          <q-btn
            label="Import"
            color="primary"
            :disable="!selectedFile"
            @click="handleImport"
          />
        </template>

        <!-- Uploading Step -->
        <template v-if="currentStep === 'uploading'">
          <q-btn label="Cancel" flat :disable="true" />
        </template>

        <!-- Preview Step -->
        <template v-if="currentStep === 'preview'">
          <q-btn label="Cancel" flat @click="closeDialog" />
          <q-btn
            label="Confirm Import"
            color="primary"
            :disable="!validationResults?.is_valid && validateBeforeImport"
            @click="confirmImport"
          />
        </template>

        <!-- Success Step -->
        <template v-if="currentStep === 'success'">
          <q-btn label="Done" color="primary" @click="closeDialog" />
        </template>

        <!-- Error Step -->
        <template v-if="currentStep === 'error'">
          <q-btn label="Close" flat @click="closeDialog" />
          <q-btn label="Try Again" color="primary" @click="resetDialog" />
        </template>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import axios from 'axios';

  // Props
  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    equipmentId: {
      type: Number,
      required: true,
    },
    siteId: {
      type: Number,
      default: null,
    },
  });

  // Emits
  const emit = defineEmits(['update:modelValue', 'import-complete']);

  // Quasar
  const $q = useQuasar();

  // Store
  const saxStore = useSaxEditorStore();

  // Refs
  const fileInput = ref(null);

  // State
  const currentStep = ref('select'); // 'select', 'uploading', 'preview', 'success', 'error'
  const selectedFile = ref(null);
  const isDragging = ref(false);
  const uploadProgress = ref(0);
  const uploadingMessage = ref('Uploading file...');
  const importMode = ref('replace'); // 'replace' or 'merge'
  const validateBeforeImport = ref(true);
  const importPreview = ref(null);
  const validationResults = ref(null);
  const importResult = ref(null);
  const errorMessage = ref('');
  const errorDetails = ref('');

  // Constants
  const importModeOptions = [
    {
      label: 'Replace existing program',
      value: 'replace',
      description: 'Remove all existing components and links',
    },
    {
      label: 'Merge with existing program',
      value: 'merge',
      description: 'Keep existing components and add new ones',
    },
  ];

  // Methods

  /**
   * Handle file drop
   */
  function handleDrop(event) {
    isDragging.value = false;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      selectFile(files[0]);
    }
  }

  /**
   * Handle file select from input
   */
  function handleFileSelect(event) {
    const files = event.target.files;
    if (files.length > 0) {
      selectFile(files[0]);
    }
  }

  /**
   * Select file and validate
   */
  function selectFile(file) {
    // Validate file type
    const validExtensions = ['.sax', '.xml'];
    const fileName = file.name.toLowerCase();
    const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValidType) {
      $q.notify({
        type: 'negative',
        message: 'Invalid file type',
        caption: 'Please select a .sax or .xml file',
      });
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      $q.notify({
        type: 'negative',
        message: 'File too large',
        caption: 'Maximum file size is 50MB',
      });
      return;
    }

    selectedFile.value = file;
  }

  /**
   * Format file size
   */
  function formatFileSize(bytes) {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Handle import
   */
  async function handleImport() {
    if (!selectedFile.value) {return;}

    currentStep.value = 'uploading';
    uploadingMessage.value = 'Uploading file...';
    uploadProgress.value = 0;

    try {
      // Import the file
      uploadingMessage.value = 'Parsing SAX file...';
      uploadProgress.value = 50;

      const result = await saxStore.importSaxFile(
        props.equipmentId,
        selectedFile.value,
        props.siteId,
      );

      importPreview.value = result;
      uploadProgress.value = 75;

      // Validate if requested
      if (validateBeforeImport.value) {
        uploadingMessage.value = 'Validating program...';
        validationResults.value = await saxStore.validateProgram(
          props.equipmentId,
          props.siteId,
        );
        uploadProgress.value = 100;
      }

      // Show preview
      currentStep.value = 'preview';
    } catch (error) {
      currentStep.value = 'error';
      errorMessage.value = error.message || 'Failed to import SAX file';
      errorDetails.value = error.response?.data || JSON.stringify(error);
    }
  }

  /**
   * Confirm import after preview
   */
  async function confirmImport() {
    currentStep.value = 'uploading';
    uploadingMessage.value = 'Finalizing import...';

    try {
      // Import is already done, just need to refresh
      await saxStore.loadSaxDataForEquipment(props.equipmentId, props.siteId);

      importResult.value = importPreview.value;
      currentStep.value = 'success';

      // Emit event
      emit('import-complete', importResult.value);

      // Auto-close after 2 seconds
      setTimeout(() => {
        closeDialog();
      }, 2000);
    } catch (error) {
      currentStep.value = 'error';
      errorMessage.value = 'Failed to finalize import';
      errorDetails.value = JSON.stringify(error);
    }
  }

  /**
   * Close dialog
   */
  function closeDialog() {
    emit('update:modelValue', false);
    // Reset after close animation
    setTimeout(() => {
      resetDialog();
    }, 300);
  }

  /**
   * Reset dialog to initial state
   */
  function resetDialog() {
    currentStep.value = 'select';
    selectedFile.value = null;
    isDragging.value = false;
    uploadProgress.value = 0;
    uploadingMessage.value = 'Uploading file...';
    importMode.value = 'replace';
    validateBeforeImport.value = true;
    importPreview.value = null;
    validationResults.value = null;
    importResult.value = null;
    errorMessage.value = '';
    errorDetails.value = '';

    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }

  // Watchers

  /**
   * Reset when dialog opens
   */
  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue) {
        resetDialog();
      }
    },
  );
</script>

<style scoped lang="scss">
.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    border-color: #1976d2;
    background: #f5f5f5;
  }

  &-active {
    border-color: #1976d2;
    background: #e3f2fd;
  }
}

.file-info {
  animation: slideIn 0.3s ease-out;
}

.import-options {
  animation: slideIn 0.3s ease-out;
}

.error-details {
  pre {
    font-size: 0.7rem;
    overflow-x: auto;
    max-height: 200px;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
